import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Copy, KeyRound, Mail, MoreHorizontal, Plus, Trash2, UserPlus } from 'lucide-react';
import type { Client, PresenterMode, Role, UserListItem } from '../../shared/types';
import { api, del, patch, post, uploadLogo } from '../lib/api';
import { relativeTime } from '../lib/format';
import { useConfig, useMe } from '../lib/session';
import { useTitle } from '../components/AppShell';
import { errorMessage, useToast } from '../components/toast';
import { ConfirmDialog, Dialog, EmptyState, Field, LogoTile, Menu, Notice, Spinner } from '../components/ui';

const ROLE_LABEL: Record<Role, string> = { admin: 'Admin', member: 'Agentur', client: 'Kunde' };

function useCopy() {
  const toast = useToast();
  return async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast('Link kopiert.');
    } catch {
      toast(text);
    }
  };
}

// ---------- Einladen ----------

export function InviteDialog({
  open,
  onClose,
  clients,
  fixedClientId,
  allowAgency,
}: {
  open: boolean;
  onClose: () => void;
  clients: Client[];
  fixedClientId?: string;
  allowAgency: boolean;
}) {
  const queryClient = useQueryClient();
  const copy = useCopy();
  const { data: config } = useConfig();
  const mailEnabled = Boolean(config?.mailEnabled);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>(fixedClientId || !allowAgency ? 'client' : 'member');
  const [clientId, setClientId] = useState(fixedClientId ?? '');
  const [send, setSend] = useState(true);
  const [result, setResult] = useState<{ url: string; mailed: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setEmail('');
    setName('');
    setRole(fixedClientId || !allowAgency ? 'client' : 'member');
    setClientId(fixedClientId ?? clients[0]?.id ?? '');
    setSend(true);
    setResult(null);
    setError(null);
  }, [open, fixedClientId, allowAgency, clients]);

  const resultText = !result
    ? ''
    : result.mailed
      ? 'Die Einladung ist per E-Mail unterwegs.'
      : !mailEnabled
        ? 'Einladung angelegt. Es ist kein Mailversand eingerichtet – bitte gib den Link selbst weiter (z. B. per Teams).'
        : send
          ? 'Einladung angelegt, aber die Mail ging nicht raus. Bitte gib den Link selbst weiter.'
          : 'Einladung angelegt. Gib den Link weiter, wann es passt.';

  const invite = useMutation({
    mutationFn: () =>
      post<{ url: string; mailed: boolean }>('/api/users/invite', {
        email,
        name,
        role,
        clientId: role === 'client' ? clientId : null,
        send: mailEnabled && send,
      }),
    onSuccess: async (data) => {
      setResult(data);
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (err) => setError(errorMessage(err)),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Person einladen"
      footer={
        result ? (
          <button className="btn btn-primary" onClick={onClose}>
            Fertig
          </button>
        ) : (
          <>
            <button className="btn btn-ghost" onClick={onClose}>
              Abbrechen
            </button>
            <button
              className="btn btn-primary"
              disabled={!email || invite.isPending || (role === 'client' && !clientId)}
              onClick={() => invite.mutate()}
            >
              Einladen
            </button>
          </>
        )
      }
    >
      {result ? (
        <div className="stack">
          <Notice kind={result.mailed || !send || !mailEnabled ? 'ok' : 'warn'}>{resultText}</Notice>
          <div className="row">
            <input className="input mono" readOnly value={result.url} onFocus={(e) => e.target.select()} />
            <button className="btn btn-outline" onClick={() => copy(result.url)}>
              <Copy size={16} /> Kopieren
            </button>
          </div>
          <p className="small muted">
            Der Link ist 14 Tage gültig. Mit Microsoft-Konto kann die Person die Einladung auch direkt per SSO
            annehmen.
          </p>
        </div>
      ) : (
        <>
          {error && <Notice kind="error">{error}</Notice>}
          <div className="form-grid two">
            <Field label="E-Mail">
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </Field>
            <Field label="Name (optional)">
              {(id) => (
                <input id={id} className="input" value={name} onChange={(e) => setName(e.target.value)} />
              )}
            </Field>
          </div>
          {!fixedClientId && (
            <Field label="Rolle">
              {(id) => (
                <select
                  id={id}
                  className="select"
                  value={role}
                  onChange={(e) => setRole(e.target.value as Role)}
                >
                  {allowAgency && (
                    <option value="admin">Admin – verwaltet Team, Kunden und alles andere</option>
                  )}
                  {allowAgency && (
                    <option value="member">Agentur – Kampagnen, Uploads, interne Kommentare</option>
                  )}
                  <option value="client">Kunde – sieht freigeschaltete Kampagnen seines Unternehmens</option>
                </select>
              )}
            </Field>
          )}
          {role === 'client' && !fixedClientId && (
            <Field label="Kunde">
              {(id) => (
                <select
                  id={id}
                  className="select"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                >
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}
          {mailEnabled && (
            <label className="check">
              <input type="checkbox" checked={send} onChange={(e) => setSend(e.target.checked)} />
              <span>
                Einladung per E-Mail schicken
                <span className="small muted" style={{ display: 'block' }}>
                  Ohne Haken bekommst du nur den Link und gibst ihn selbst weiter.
                </span>
              </span>
            </label>
          )}
        </>
      )}
    </Dialog>
  );
}

function UserActions({
  user,
  isSelf,
  allowAgency,
}: {
  user: UserListItem;
  isSelf: boolean;
  allowAgency: boolean;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const copy = useCopy();
  const { data: config } = useConfig();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['users'] });
  const invited = user.status === 'invited';
  const mailLink = async () => {
    const res = await post<{ url: string; mailed: boolean }>(`/api/users/${user.id}/link`, { send: true });
    if (res.mailed) {
      toast(
        invited
          ? `Einladung an ${user.email} verschickt.`
          : `Link zum Passwort-Setzen an ${user.email} verschickt.`,
      );
    } else {
      await copy(res.url);
      toast('Die Mail ging nicht raus – der Link liegt in der Zwischenablage.', 'error');
    }
  };
  const run = async (fn: () => Promise<unknown>, message?: string) => {
    try {
      await fn();
      await refresh();
      if (message) toast(message);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  return (
    <>
      <Menu
        trigger={(props) => (
          <button
            {...props}
            className="btn btn-ghost btn-sm btn-icon"
            aria-label={`Aktionen für ${user.email}`}
          >
            <MoreHorizontal size={18} />
          </button>
        )}
      >
        {(close) => (
          <>
            {user.status !== 'disabled' && (
              <button
                className="menu-item"
                onClick={() =>
                  run(async () => {
                    close();
                    const res = await post<{ type: string; url: string; mailed: boolean }>(
                      `/api/users/${user.id}/link`,
                    );
                    await copy(res.url);
                  })
                }
              >
                <KeyRound size={16} />{' '}
                {invited ? 'Neuen Einladungslink kopieren' : 'Link zum Passwort-Setzen kopieren'}
              </button>
            )}
            {user.status !== 'disabled' && config?.mailEnabled && (
              <button
                className="menu-item"
                onClick={() =>
                  run(async () => {
                    close();
                    await mailLink();
                  })
                }
              >
                <Mail size={16} />{' '}
                {invited ? 'Einladung per E-Mail schicken' : 'Passwort-Link per E-Mail schicken'}
              </button>
            )}
            {allowAgency &&
              !isSelf &&
              (['admin', 'member', 'client'] as Role[])
                .filter((r) => r !== user.role && r !== 'client')
                .map((r) => (
                  <button
                    key={r}
                    className="menu-item"
                    onClick={() =>
                      run(() => (close(), patch(`/api/users/${user.id}`, { role: r })), 'Rolle geändert.')
                    }
                  >
                    Rolle: {ROLE_LABEL[r]}
                  </button>
                ))}
            {!isSelf && user.status === 'active' && (
              <button
                className="menu-item"
                onClick={() =>
                  run(
                    () => (close(), patch(`/api/users/${user.id}`, { status: 'disabled' })),
                    'Zugang deaktiviert.',
                  )
                }
              >
                Deaktivieren
              </button>
            )}
            {!isSelf && user.status === 'disabled' && (
              <button
                className="menu-item"
                onClick={() =>
                  run(
                    () => (close(), patch(`/api/users/${user.id}`, { status: 'active' })),
                    'Zugang aktiviert.',
                  )
                }
              >
                Wieder aktivieren
              </button>
            )}
            {!isSelf && (
              <>
                <div className="menu-sep" />
                <button
                  className="menu-item danger"
                  onClick={() => {
                    close();
                    setConfirmDelete(true);
                  }}
                >
                  <Trash2 size={16} /> Löschen
                </button>
              </>
            )}
          </>
        )}
      </Menu>
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Zugang löschen?"
        message={`${user.name || user.email} verliert sofort den Zugang. Kommentare und Freigaben bleiben mit Namen erhalten.`}
        onConfirm={() => run(() => del(`/api/users/${user.id}`), 'Zugang gelöscht.')}
      />
    </>
  );
}

function StatusLabel({ user }: { user: UserListItem }) {
  if (user.status === 'invited') return <span className="chip chip-info">Eingeladen</span>;
  if (user.status === 'disabled') return <span className="chip chip-changes_requested">Deaktiviert</span>;
  return <span className="chip chip-approved">Aktiv</span>;
}

function loginMethods(user: UserListItem): string {
  const methods = [
    user.identities.includes('microsoft') && 'Microsoft',
    user.identities.includes('oidc') && 'SSO',
    user.hasPassword && 'Passwort',
  ];
  return methods.filter(Boolean).join(', ') || '–';
}

// ---------- Team ----------

export function TeamPage() {
  useTitle('Team & Zugänge');
  const { data: me } = useMe();
  const { data: config } = useConfig();
  const [inviting, setInviting] = useState(false);
  const users = useQuery({ queryKey: ['users'], queryFn: () => api<UserListItem[]>('/api/users') });
  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api<Client[]>('/api/clients') });
  const [filter, setFilter] = useState<'all' | 'agency' | 'client'>('all');
  const list = (users.data ?? []).filter(
    (u) => filter === 'all' || (filter === 'agency' ? u.role !== 'client' : u.role === 'client'),
  );
  const microsoft = config?.providers.some((p) => p.id === 'microsoft');

  return (
    <div className="page">
      <div className="page-head">
        <div className="page-head-text">
          <span className="eyebrow">Verwaltung</span>
          <h1 className="page-title">Team & Zugänge</h1>
          <p className="lead">
            Wer sich anmelden darf und was er sieht. Agentur-Konten sehen alle Kampagnen, Kundenkonten nur die
            freigeschalteten ihres Unternehmens.
          </p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => setInviting(true)}>
            <UserPlus size={18} /> Person einladen
          </button>
        </div>
      </div>
      <div className="stack" style={{ gap: 10, marginBottom: 22 }}>
        <Notice kind={microsoft ? 'ok' : 'info'}>
          {microsoft
            ? 'Anmeldung mit Microsoft ist aktiv. Eingeladene Personen können ihr Firmenkonto nutzen; neue Konten entstehen nur per Einladung oder über freigegebene Domains.'
            : 'Anmeldung mit Microsoft ist noch nicht eingerichtet. Bis dahin melden sich alle mit E-Mail und Passwort an (siehe docs/microsoft-sso.md).'}
        </Notice>
      </div>
      <div className="filters">
        <div className="segmented" role="group" aria-label="Filter">
          <button aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
            Alle
          </button>
          <button aria-pressed={filter === 'agency'} onClick={() => setFilter('agency')}>
            Agentur
          </button>
          <button aria-pressed={filter === 'client'} onClick={() => setFilter('client')}>
            Kunden
          </button>
        </div>
      </div>
      {users.isLoading && <Spinner />}
      {users.error && <Notice kind="error">{errorMessage(users.error)}</Notice>}
      {list.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Rolle</th>
                <th>Anmeldung</th>
                <th>Status</th>
                <th>Zuletzt aktiv</th>
                <th aria-label="Aktionen" />
              </tr>
            </thead>
            <tbody>
              {list.map((u) => (
                <tr key={u.id}>
                  <td>
                    <strong>{u.name || '–'}</strong>
                    <div className="small muted">{u.email}</div>
                  </td>
                  <td>
                    {ROLE_LABEL[u.role]}
                    {u.client && <div className="small muted">{u.client.name}</div>}
                  </td>
                  <td className="small">{loginMethods(u)}</td>
                  <td>
                    <StatusLabel user={u} />
                  </td>
                  <td className="small muted">{u.lastLoginAt ? relativeTime(u.lastLoginAt) : 'nie'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <UserActions user={u} isSelf={u.id === me?.id} allowAgency={me?.role === 'admin'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <InviteDialog
        open={inviting}
        onClose={() => setInviting(false)}
        clients={clients.data ?? []}
        allowAgency={me?.role === 'admin'}
      />
    </div>
  );
}

// ---------- Kunden ----------

function ClientDialog({ open, onClose, client }: { open: boolean; onClose: () => void; client?: Client }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: config } = useConfig();
  const [name, setName] = useState('');
  const [color, setColor] = useState('');
  const [domains, setDomains] = useState('');
  const [logo, setLogo] = useState<File | null>(null);
  const [presenterMode, setPresenterMode] = useState<PresenterMode>('agency');
  const [presenterName, setPresenterName] = useState('');
  const [presenterLogo, setPresenterLogo] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const agencyName = config?.branding.providerName || config?.appName || 'Agentur';

  useEffect(() => {
    if (!open) return;
    setName(client?.name ?? '');
    setColor(client?.brandColor ?? '');
    setDomains(client?.emailDomains.join(', ') ?? '');
    setLogo(null);
    setPresenterMode(client?.presenter.mode ?? 'agency');
    setPresenterName(client?.presenter.name ?? '');
    setPresenterLogo(null);
    setError(null);
  }, [open, client]);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = {
        name,
        brandColor: color || null,
        emailDomains: domains
          .split(/[\s,;]+/)
          .map((d) => d.trim().replace(/^@/, ''))
          .filter(Boolean),
        presenterMode,
        presenterName,
      };
      const saved = client
        ? await patch<{ id: string }>(`/api/clients/${client.id}`, body)
        : await post<{ id: string }>('/api/clients', body);
      if (logo) await uploadLogo(saved.id, logo);
      if (presenterLogo) await uploadLogo(saved.id, presenterLogo, 'presenter');
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast(client ? 'Kunde gespeichert.' : 'Kunde angelegt.');
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={client ? 'Kunde bearbeiten' : 'Neuer Kunde'}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn btn-primary" disabled={!name.trim() || busy} onClick={save}>
            Speichern
          </button>
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      <Field label="Name">
        {(id) => (
          <input id={id} className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
        )}
      </Field>
      {client?.logoUrl && !logo && (
        <div className="row">
          <LogoTile src={client.logoUrl} alt={client.name} width={160} height={56} plate />
          <span className="small muted">Aktuelles Logo – zum Ersetzen unten eine neue Datei wählen.</span>
        </div>
      )}
      <div className="form-grid two">
        <Field label="Logo" hint="PNG, JPG, WebP oder SVG, max. 2 MB – erscheint groß auf Freigabe-Links">
          {(id) => (
            <input
              id={id}
              className="input"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={(e) => setLogo(e.target.files?.[0] ?? null)}
            />
          )}
        </Field>
        <Field
          label="Markenfarbe (optional)"
          hint="Färbt die Freigabe-Seiten ein: Linien, Links, Hervorhebungen"
        >
          {(id) => (
            <div className="row">
              <input
                type="color"
                aria-label="Farbe wählen"
                value={color || '#000000'}
                onChange={(e) => setColor(e.target.value)}
                style={{ width: 42, height: 42, border: 0, padding: 0, background: 'none' }}
              />
              <input
                id={id}
                className="input mono"
                value={color}
                placeholder="#RRGGBB"
                onChange={(e) => setColor(e.target.value)}
              />
            </div>
          )}
        </Field>
      </div>
      <fieldset className="presenter-field">
        <legend>Absender auf Freigabe-Links</legend>
        <p className="small muted">
          Wer die Banner präsentiert. Arbeitet ihr als Dienstleister für eine andere Agentur, kann deren Logo
          statt eurem erscheinen.
        </p>
        {(
          [
            ['agency', `${agencyName} (ihr)`, 'Euer Logo steht als Absender neben dem Kundenlogo.'],
            [
              'partner',
              'Partner',
              'Name und Logo einer anderen Agentur, z. B. wenn sie die Freigabe an ihren Kunden weiterreicht.',
            ],
            ['none', 'Kein Absender', 'Nur das Kundenlogo – ohne Hinweis auf euch.'],
          ] as [PresenterMode, string, string][]
        ).map(([mode, title, text]) => (
          <label key={mode} className="check">
            <input
              type="radio"
              name="presenter"
              checked={presenterMode === mode}
              onChange={() => setPresenterMode(mode)}
            />
            <span>
              <strong>{title}</strong>
              <span className="small muted" style={{ display: 'block' }}>
                {text}
              </span>
            </span>
          </label>
        ))}
        {presenterMode === 'partner' && (
          <div className="form-grid two" style={{ marginTop: 8 }}>
            <Field label="Name des Partners">
              {(id) => (
                <input
                  id={id}
                  className="input"
                  value={presenterName}
                  maxLength={120}
                  onChange={(e) => setPresenterName(e.target.value)}
                />
              )}
            </Field>
            <Field
              label="Logo des Partners"
              hint={
                client?.presenter.logoUrl && !presenterLogo
                  ? 'Vorhanden – zum Ersetzen neue Datei wählen'
                  : 'PNG, JPG, WebP oder SVG'
              }
            >
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={(e) => setPresenterLogo(e.target.files?.[0] ?? null)}
                />
              )}
            </Field>
            {client?.presenter.logoUrl && !presenterLogo && (
              <LogoTile
                src={client.presenter.logoUrl}
                alt={client.presenter.name}
                width={140}
                height={44}
                plate
              />
            )}
          </div>
        )}
      </fieldset>
      <Field
        label="E-Mail-Domains für Microsoft-Anmeldung (optional)"
        hint="Wer sich mit einer dieser Domains per Microsoft anmeldet, bekommt automatisch ein Kundenkonto. Leer lassen = nur eingeladene Personen."
      >
        {(id) => (
          <input
            id={id}
            className="input"
            placeholder="kunde.de, kunde-gruppe.com"
            value={domains}
            onChange={(e) => setDomains(e.target.value)}
          />
        )}
      </Field>
    </Dialog>
  );
}

export function ClientsPage() {
  useTitle('Kunden');
  const { data: me } = useMe();
  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api<Client[]>('/api/clients') });
  const users = useQuery({ queryKey: ['users'], queryFn: () => api<UserListItem[]>('/api/users') });
  const [editing, setEditing] = useState<Client | 'new' | null>(null);
  const [inviteFor, setInviteFor] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Client | null>(null);
  const queryClient = useQueryClient();
  const toast = useToast();

  const usersByClient = useMemo(() => {
    const map = new Map<string, UserListItem[]>();
    for (const u of users.data ?? [])
      if (u.client) map.set(u.client.id, [...(map.get(u.client.id) ?? []), u]);
    return map;
  }, [users.data]);

  return (
    <div className="page">
      <div className="page-head">
        <div className="page-head-text">
          <span className="eyebrow">Verwaltung</span>
          <h1 className="page-title">Kunden</h1>
          <p className="lead">Kunden bündeln Kampagnen und die Personen, die sie freigeben dürfen.</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <Plus size={18} /> Neuer Kunde
          </button>
        </div>
      </div>
      {clients.isLoading && <Spinner />}
      {clients.data?.length === 0 && (
        <EmptyState icon={<Building2 size={34} />} title="Noch keine Kunden">
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <Plus size={18} /> Ersten Kunden anlegen
          </button>
        </EmptyState>
      )}
      <div className="campaign-list" style={{ borderTopWidth: clients.data?.length ? undefined : 0 }}>
        {clients.data?.map((client) => {
          const people = usersByClient.get(client.id) ?? [];
          return (
            <div
              key={client.id}
              className="row-wrap"
              style={{
                padding: '20px 4px',
                borderBottom: '1px solid var(--line)',
                alignItems: 'flex-start',
                gap: 20,
              }}
            >
              {client.logoUrl ? (
                <LogoTile
                  src={client.logoUrl}
                  alt={client.name}
                  width={132}
                  height={52}
                  align="center"
                  plate
                />
              ) : (
                <span
                  className="avatar"
                  style={{
                    background: client.brandColor ?? undefined,
                    borderRadius: 'var(--radius-sm)',
                    width: 132,
                    height: 52,
                  }}
                >
                  {client.name.slice(0, 2).toUpperCase()}
                </span>
              )}
              <div className="grow stack" style={{ gap: 6 }}>
                <div className="row-wrap">
                  <h3 style={{ fontSize: 19, fontWeight: 700 }}>{client.name}</h3>
                  {client.brandColor && (
                    <span
                      className="dot"
                      style={{ background: client.brandColor, width: 12, height: 12 }}
                      title={client.brandColor}
                    />
                  )}
                </div>
                <span className="small muted">
                  {client.campaignCount} Kampagnen · {people.length} Personen
                  {client.emailDomains.length > 0 && ` · SSO-Domains: ${client.emailDomains.join(', ')}`}
                  {client.presenter.mode === 'partner' &&
                    ` · Freigaben im Namen von ${client.presenter.name || 'Partner'}`}
                  {client.presenter.mode === 'none' && ' · Freigaben ohne Absender'}
                </span>
                {people.length > 0 && (
                  <div className="row-wrap small" style={{ gap: 6 }}>
                    {people.map((p) => (
                      <span
                        key={p.id}
                        className={`chip ${p.status === 'active' ? 'chip-outline' : p.status === 'invited' ? 'chip-info' : 'chip-changes_requested'}`}
                        style={{ textTransform: 'none', letterSpacing: 0 }}
                      >
                        {p.name || p.email}
                        {p.status === 'invited' && ' (eingeladen)'}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="row">
                <button className="btn btn-outline btn-sm" onClick={() => setInviteFor(client.id)}>
                  <UserPlus size={15} /> Person einladen
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(client)}>
                  Bearbeiten
                </button>
                {me?.role === 'admin' && client.campaignCount === 0 && (
                  <button
                    className="btn btn-ghost btn-sm btn-icon"
                    aria-label="Löschen"
                    onClick={() => setDeleting(client)}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <ClientDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        client={editing && editing !== 'new' ? editing : undefined}
      />
      <InviteDialog
        open={inviteFor !== null}
        onClose={() => setInviteFor(null)}
        clients={clients.data ?? []}
        fixedClientId={inviteFor ?? undefined}
        allowAgency={false}
      />
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Kunden löschen?"
        message={`„${deleting?.name}“ wird gelöscht; Kundenkonten werden deaktiviert.`}
        onConfirm={async () => {
          try {
            await del(`/api/clients/${deleting!.id}`);
            await queryClient.invalidateQueries({ queryKey: ['clients'] });
            toast('Kunde gelöscht.');
          } catch (err) {
            toast(errorMessage(err), 'error');
          }
        }}
      />
    </div>
  );
}

// ---------- Profil ----------

export function ProfilePage() {
  useTitle('Profil');
  const { data: me } = useMe();
  const { data: config } = useConfig();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(me?.name ?? '');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setName(me?.name ?? ''), [me?.name]);
  if (!me) return <Spinner />;
  return (
    <div className="page page-narrow">
      <div className="page-head">
        <div className="page-head-text">
          <span className="eyebrow">
            {ROLE_LABEL[me.role]}
            {me.clientName ? ` · ${me.clientName}` : ''}
          </span>
          <h1 className="page-title">Profil</h1>
        </div>
      </div>
      <section className="section-head" style={{ marginBottom: 16 }}>
        <h2 className="section-title">Angaben</h2>
      </section>
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            const { user } = await patch<{ user: typeof me }>('/api/auth/profile', { name });
            queryClient.setQueryData(['me'], user);
            toast('Gespeichert.');
          } catch (err) {
            toast(errorMessage(err), 'error');
          }
        }}
      >
        <Field label="Name">
          {(id) => <input id={id} className="input" value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label="E-Mail" hint="Die Adresse ändert ein Admin.">
          {(id) => <input id={id} className="input" value={me.email} disabled />}
        </Field>
        <p className="small muted">
          Anmeldung:{' '}
          {[me.identities.includes('microsoft') && 'Microsoft', me.hasPassword && 'Passwort']
            .filter(Boolean)
            .join(', ') || '–'}
        </p>
        <button className="btn btn-primary" style={{ alignSelf: 'flex-start' }}>
          Speichern
        </button>
      </form>

      {me.role !== 'client' && (
        <>
          <section className="section-head" style={{ margin: '40px 0 16px' }}>
            <h2 className="section-title">Benachrichtigungen</h2>
          </section>
          <label className="check">
            <input
              type="checkbox"
              checked={me.emailNotifications}
              onChange={async (e) => {
                try {
                  const { user } = await patch<{ user: typeof me }>('/api/auth/profile', {
                    emailNotifications: e.target.checked,
                  });
                  queryClient.setQueryData(['me'], user);
                  toast(
                    user.emailNotifications
                      ? 'E-Mail-Benachrichtigungen an.'
                      : 'E-Mail-Benachrichtigungen aus.',
                  );
                } catch (err) {
                  toast(errorMessage(err), 'error');
                }
              }}
            />
            <span>
              E-Mail bei Kundenfeedback
              <span className="field-hint" style={{ display: 'block' }}>
                Für Kampagnen, die du angelegt hast: Kommentare, Freigaben und Änderungswünsche von
                Kund:innen, gesammelt in einer Mail wenige Minuten nach dem ersten Feedback.
                {config &&
                  !config.mailEnabled &&
                  ' (Der Mailversand ist auf diesem Server noch nicht eingerichtet.)'}
              </span>
            </span>
          </label>
        </>
      )}

      <section className="section-head" style={{ margin: '40px 0 16px' }}>
        <h2 className="section-title">{me.hasPassword ? 'Passwort ändern' : 'Passwort festlegen'}</h2>
      </section>
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          if (next !== repeat) {
            setError('Die Passwörter stimmen nicht überein.');
            return;
          }
          try {
            await post('/api/auth/password', { currentPassword: current || undefined, newPassword: next });
            setCurrent('');
            setNext('');
            setRepeat('');
            await queryClient.invalidateQueries({ queryKey: ['me'] });
            toast('Passwort gespeichert. Andere Sitzungen wurden abgemeldet.');
          } catch (err) {
            setError(errorMessage(err));
          }
        }}
      >
        {error && <Notice kind="error">{error}</Notice>}
        {me.hasPassword && (
          <Field label="Aktuelles Passwort">
            {(id) => (
              <input
                id={id}
                className="input"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
              />
            )}
          </Field>
        )}
        <Field label="Neues Passwort" hint="Mindestens 10 Zeichen.">
          {(id) => (
            <input
              id={id}
              className="input"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          )}
        </Field>
        <Field label="Wiederholen">
          {(id) => (
            <input
              id={id}
              className="input"
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            />
          )}
        </Field>
        <button className="btn btn-primary" style={{ alignSelf: 'flex-start' }} disabled={!next}>
          Passwort speichern
        </button>
      </form>

      <section className="section-head" style={{ margin: '40px 0 16px' }}>
        <h2 className="section-title">Sitzungen</h2>
      </section>
      <p className="muted" style={{ marginBottom: 12 }}>
        Meldet dich auf allen Geräten ab, auch auf diesem.
      </p>
      <button
        className="btn btn-outline"
        onClick={async () => {
          await post('/api/auth/logout-all');
          queryClient.clear();
          window.location.href = '/anmelden';
        }}
      >
        Überall abmelden
      </button>
    </div>
  );
}
